import { ownerRoleIsUnassigned } from './incidentPatternVocabulary';
import { GROUPED_SAFETY_REPORT_BLOCKED_SUMMARY } from './groupedSafetyCaseStepLabels';
import { GROUPED_REPORT_READINESS_PENDING_LABEL } from './groupedReviewWorkflowUx';

export type GroupedReportSignOffInput = {
  representativeReviewComplete?: boolean;
  evidenceConfirmed?: boolean;
  patternOwnerLabel?: string | null;
  followUpOwnerLabel?: string | null;
  correctiveActionComplete?: boolean;
  followUpMeasured?: boolean;
  /** When true, impact tracking blocker is waived for report readiness (Epic 94h). */
  impactWaived?: boolean;
};

export type CanonicalClaimReadinessControl = {
  control_id?: string | null;
  label?: string | null;
  status?: string | null;
  blocking?: boolean | null;
};

/** Evidence and risk-owner gates that stay outside canonical claim-readiness controls. */
export function buildGroupedEvidenceGateMissingItems(
  args: Pick<GroupedReportSignOffInput, 'representativeReviewComplete' | 'evidenceConfirmed' | 'patternOwnerLabel'>,
): string[] {
  const items: string[] = [];
  if (!args.representativeReviewComplete) {
    items.push('Complete evidence review');
    return items;
  }
  if (!args.evidenceConfirmed) {
    items.push('Confirm risk pattern in evidence review');
    return items;
  }
  if (ownerRoleIsUnassigned(args.patternOwnerLabel)) {
    items.push('Assign risk owner');
  }
  return items;
}

export function buildCanonicalClaimReadinessMissingItems(
  controls: CanonicalClaimReadinessControl[] | null | undefined,
): string[] {
  return (Array.isArray(controls) ? controls : [])
    .filter((control) => {
      if (control.blocking === false) return false;
      const status = String(control.status || '').trim().toLowerCase();
      return status && status !== 'resolved' && status !== 'not_applicable';
    })
    .map((control) => String(control.label || control.control_id || '').trim())
    .filter(Boolean);
}

export function isCanonicalClaimControlResolved(
  controls: CanonicalClaimReadinessControl[] | null | undefined,
  controlId: string,
): boolean {
  const control = (Array.isArray(controls) ? controls : []).find(
    (item) => String(item.control_id || '').trim() === controlId,
  );
  if (!control) return false;
  const status = String(control.status || '').trim().toLowerCase();
  return status === 'resolved' || status === 'not_applicable' || control.blocking === false;
}

export function mergeGroupedSignOffMissingItems(args: {
  evidenceGateItems: string[];
  canonicalClaimItems: string[];
  legacyItems: string[];
  useCanonicalClaimReadiness: boolean;
}): string[] {
  if (!args.useCanonicalClaimReadiness) return args.legacyItems;
  if (
    args.evidenceGateItems.some(
      (item) => item === 'Complete evidence review' || item.includes('Confirm risk pattern'),
    )
  ) {
    return args.evidenceGateItems;
  }
  return [...args.evidenceGateItems, ...args.canonicalClaimItems];
}

/** Ordered blockers for grouped report preparation / Safety Report sign-off. */
export function buildGroupedReportSignOffMissingItems(
  args: GroupedReportSignOffInput,
): string[] {
  const items: string[] = [];
  if (!args.representativeReviewComplete) {
    items.push('Complete evidence review');
    return items;
  }
  if (!args.evidenceConfirmed) {
    items.push('Confirm risk pattern in evidence review');
    return items;
  }
  if (ownerRoleIsUnassigned(args.patternOwnerLabel)) {
    items.push('Assign risk owner');
  }
  if (ownerRoleIsUnassigned(args.followUpOwnerLabel)) {
    items.push('Assign action owner');
  }
  if (!args.correctiveActionComplete) {
    items.push('Complete or validate corrective action');
  }
  if (!args.followUpMeasured && !args.impactWaived) {
    items.push('Complete or explicitly waive impact measurement');
  }
  return items;
}

export function buildGroupedReportReadinessPresentation(args: {
  signOffMissingItems: string[];
}): {
  blocked: boolean;
  readinessLabel: string;
  blockedReason: string;
  readinessHelper: string;
} {
  const blocked = args.signOffMissingItems.length > 0;
  const missing = args.signOffMissingItems.join('; ');
  const missingCsv = args.signOffMissingItems.join(', ');
  return {
    blocked,
    readinessLabel: blocked ? GROUPED_REPORT_READINESS_PENDING_LABEL : 'Ready',
    blockedReason: blocked
      ? `${GROUPED_SAFETY_REPORT_BLOCKED_SUMMARY} Missing: ${missing}.`
      : '',
    readinessHelper: blocked
      ? `${GROUPED_SAFETY_REPORT_BLOCKED_SUMMARY} Outstanding: ${missingCsv}.`
      : 'Evidence review, corrective action closure, and impact measurement are recorded for final Safety Report sign-off.',
  };
}
